import React, { useMemo, useCallback } from 'react';
import { Row, Col, Card, Spin, message } from 'antd';
import {
  MessageOutlined,
  VideoCameraOutlined,
  PhoneOutlined,
  QuestionCircleOutlined,
  FolderOutlined,
  FileTextOutlined,
  SettingOutlined,
  CalendarOutlined,
  ShoppingOutlined,
  BookOutlined,
  RocketOutlined,
  PlayCircleOutlined,
  CreditCardOutlined,
  LaptopOutlined,
  MailOutlined,
  TeamOutlined,
  ApiOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import styles from './HelpCards.module.css';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCategoriesWithFolders, usePrefetch } from '../../hooks/useFaqCache';

const HelpCards = React.memo(() => {
  const { t, i18n } = useTranslation();
  const { prefetchFolder } = usePrefetch();
  
  // Yeni optimize edilmiş endpoint ile veri çek
  const { 
    data: categoriesData, 
    isLoading, 
    isError, 
    error 
  } = useCategoriesWithFolders(i18n.language);
  
  // Prefetch popular folders for better performance
  React.useEffect(() => {
    if (categoriesData?.data?.categories) {
      const categories = categoriesData.data.categories;
      // Prefetch first few folders for instant navigation
      categories.slice(0, 3).forEach(category => {
        if (category.folders) {
          category.folders.slice(0, 2).forEach(folder => {
            prefetchFolder(category.id, folder.id, i18n.language);
          });
        }
      });
    }
  }, [categoriesData, prefetchFolder, i18n.language]);

  // Veriyi işle - memoized
  const categories = useMemo(() => categoriesData?.data?.categories || [], [categoriesData]);
  const validCategories = useMemo(() => 
    categories.filter(cat => cat && typeof cat === 'object' && cat.id && cat.name),
    [categories]
  );

  // Her kategori için klasörleri hazırla - memoized
  const categoryFolders = useMemo(() => {
    const folders = {};
    validCategories.forEach(category => {
      if (category.folders && Array.isArray(category.folders)) {
        folders[category.id] = category.folders;
      }
    });
    return folders;
  }, [validCategories]);

  // Hata durumunda mesaj göster
  if (isError) {
    message.error('Failed to load FAQ data. Please try again.');
  }

  // Icon mapping for different types based on folder names - memoized
  const getIconForItem = useCallback((name, index) => {
    const folderName = name.toLowerCase();
    
    // Akıllı ikon eşleştirme sistemi
    if (folderName.includes('payment') || folderName.includes('billing') || folderName.includes('credit')) {
      return <CreditCardOutlined key="payment" style={{ fontSize: '32px', color: '#667eea' }} />;
    }
    if (folderName.includes('video') || folderName.includes('tutorial') || folderName.includes('guide')) {
      return <PlayCircleOutlined key="video" style={{ fontSize: '32px', color: '#667eea' }} />;
    }
    if (folderName.includes('email') || folderName.includes('mail') || folderName.includes('notification')) {
      return <MailOutlined key="email" style={{ fontSize: '32px', color: '#667eea' }} />;
    }
    if (folderName.includes('customer') || folderName.includes('client') || folderName.includes('user')) {
      return <TeamOutlined key="customer" style={{ fontSize: '32px', color: '#667eea' }} />;
    }
    if (folderName.includes('setting') || folderName.includes('config') || folderName.includes('setup')) {
      return <SettingOutlined key="settings" style={{ fontSize: '32px', color: '#667eea' }} />;
    }
    if (folderName.includes('calendar') || folderName.includes('schedule') || folderName.includes('booking')) {
      return <CalendarOutlined key="calendar" style={{ fontSize: '32px', color: '#667eea' }} />;
    }
    if (folderName.includes('order') || folderName.includes('booking') || folderName.includes('reservation')) {
      return <ShoppingOutlined key="order" style={{ fontSize: '32px', color: '#667eea' }} />;
    }
    if (folderName.includes('inventory') || folderName.includes('product') || folderName.includes('stock')) {
      return <FileTextOutlined key="inventory" style={{ fontSize: '32px', color: '#667eea' }} />;
    }
    if (folderName.includes('api') || folderName.includes('integration') || folderName.includes('connect')) {
      return <ApiOutlined key="api" style={{ fontSize: '32px', color: '#667eea' }} />;
    }
    if (folderName.includes('money') || folderName.includes('revenue') || folderName.includes('earnings')) {
      return <DollarOutlined key="money" style={{ fontSize: '32px', color: '#667eea' }} />;
    }
    if (folderName.includes('laptop') || folderName.includes('computer') || folderName.includes('device')) {
      return <LaptopOutlined key="laptop" style={{ fontSize: '32px', color: '#667eea' }} />;
    }
    if (folderName.includes('start') || folderName.includes('begin') || folderName.includes('first')) {
      return <RocketOutlined key="start" style={{ fontSize: '32px', color: '#667eea' }} />;
    }
    
    // Eğer hiçbir eşleşme bulunamazsa, index'e göre varsayılan ikonlar
    const defaultIcons = [
      <BookOutlined key="book" style={{ fontSize: '32px', color: '#667eea' }} />,
      <FileTextOutlined key="file" style={{ fontSize: '32px', color: '#667eea' }} />,
      <SettingOutlined key="settings" style={{ fontSize: '32px', color: '#667eea' }} />,
      <CalendarOutlined key="calendar" style={{ fontSize: '32px', color: '#667eea' }} />,
      <ShoppingOutlined key="shopping" style={{ fontSize: '32px', color: '#667eea' }} />,
      <CreditCardOutlined key="payment" style={{ fontSize: '32px', color: '#667eea' }} />,
      <LaptopOutlined key="laptop" style={{ fontSize: '32px', color: '#667eea' }} />,
      <MailOutlined key="email" style={{ fontSize: '32px', color: '#667eea' }} />,
      <TeamOutlined key="team" style={{ fontSize: '32px', color: '#667eea' }} />,
      <ApiOutlined key="api" style={{ fontSize: '32px', color: '#667eea' }} />,
      <DollarOutlined key="dollar" style={{ fontSize: '32px', color: '#667eea' }} />,
      <RocketOutlined key="rocket" style={{ fontSize: '32px', color: '#667eea' }} />
    ];
    
    return defaultIcons[index % defaultIcons.length];
  }, []);

  // Color mapping for cards - memoized
  const getColorForIndex = useCallback((index) => {
    const colors = [
      '#667eea', // Blue
      '#764ba2', // Purple
      '#f093fb', // Pink
      '#4facfe', // Light Blue
      '#43e97b', // Green
      '#fa709a', // Rose
      '#a8edea', // Cyan
      '#fed6e3', // Light Pink
      '#ffecd2', // Orange
      '#fcb69f', // Peach
      '#ff9a9e', // Coral
      '#a18cd1', // Lavender
    ];
    return colors[index % colors.length];
  }, []);

  // Tüm kartları oluştur (sadece klasörler) - memoized
  const generateAllCards = useCallback(() => {
    const allCards = [];
    let cardIndex = 0;

    categories.forEach(category => {
      // Klasör kartlarını ekle
      const folders = categoryFolders[category.id] || [];
      folders.forEach(folder => {
        allCards.push({
          id: `folder-${folder.id}`,
          title: folder.name,
          description: '',
          icon: getIconForItem(folder.name, cardIndex),
          color: getColorForIndex(cardIndex),
          link: `/faq/category/${category.id}?name=${encodeURIComponent(category.name)}&folder=${folder.id}`,
          type: 'folder'
        });
        cardIndex++;
      });
    });

    return allCards;
  }, [categories, categoryFolders, getIconForItem, getColorForIndex]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.loadingContainer}>
            <Spin size="large" />
            <p>Loading FAQ categories...</p>
            <small style={{ color: '#666', marginTop: '8px' }}>
              This may take a few seconds on first load
            </small>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.errorContainer}>
            <h3>Failed to Load FAQ Data</h3>
            <p>Unable to load FAQ categories. Please try again later.</p>
          </div>
        </div>
      </div>
    );
  }

  const allCards = generateAllCards();
  const sectionTitle = categories.length > 0 ? categories[0].name : 'FAQ Categories';

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {categories.map((category, categoryIndex) => {
          const folders = categoryFolders[category.id] || [];
          const categoryCards = folders.map((folder, folderIndex) => {
            const cardIndex = categoryIndex * 100 + folderIndex; // Unique index for each card
            
            const content = (
              <Card 
                className={styles.card} 
                hoverable
                style={{ 
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  height: '100%'
                }}
              >
                <div className={styles.cardContent}>
                  <div className={styles.icon}>
                    {getIconForItem(folder.name, cardIndex)}
                  </div>
                  <h3 className={styles.title}>{folder.name}</h3>
                  <p className={styles.description}></p>
                </div>
              </Card>
            );

            return (
              <Col xs={24} sm={12} md={8} lg={6} key={`folder-${folder.id}`} style={{ display: 'flex', justifyContent: 'center' }}>
                <Link to={`/${i18n.language}/support/solutions/folders/${folder.id}`} className={styles.link}>
                  {content}
                </Link>
              </Col>
            );
          });

          return (
            <div key={`category-${category.id}`} className={styles.categorySection}>
              <h2 className={styles.sectionTitle}>{category.name}</h2>
              <Row gutter={[24, 24]} justify="center">
                {categoryCards}
              </Row>
              {categoryIndex < categories.length - 1 && <div className={styles.sectionDivider}></div>}
            </div>
          );
        })}
      </div>
    </div>
  );
});

HelpCards.displayName = 'HelpCards';

export default HelpCards;

